import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { Alert, Empty, Kpi, KpiGrid, Muted, PageHeader, Section } from '@/components/ui'
import {
  importarColaboradoresArquivo,
  type ImportarColaboradoresResult,
  type ImportarProgresso,
} from './api'
import { ImportPipelineNav } from './ImportPipelineNav'

export function ImportarColaboradoresPage() {
  const { canEdit } = useAuth()
  const editable = canEdit('colaboradores')
  const qc = useQueryClient()

  const [file, setFile] = useState<File | null>(null)
  const [dryRun, setDryRun] = useState(true)
  const [criarFaltantes, setCriarFaltantes] = useState(false)
  const [msg, setMsg] = useState('')
  const [erro, setErro] = useState(false)
  const [pending, setPending] = useState(false)
  const [progresso, setProgresso] = useState<ImportarProgresso | null>(null)
  const [resultado, setResultado] = useState<ImportarColaboradoresResult | null>(null)

  async function executar() {
    if (!file) {
      setErro(true)
      setMsg('Selecione um arquivo .wk1, .xls ou .csv.')
      return
    }
    setPending(true)
    setErro(false)
    setMsg('Enviando arquivo…')
    setProgresso(null)
    setResultado(null)
    try {
      const res = await importarColaboradoresArquivo(file, {
        dryRun,
        criarFaltantes,
        onProgress: (p) => {
          setProgresso(p)
          setMsg(
            `Processando lote ${p.loteAtual}/${p.lotes} — ${p.done} de ${p.total} registros…`,
          )
        },
      })
      setResultado(res)
      setProgresso({
        done: res.total ?? res.lidos ?? 0,
        total: res.total ?? res.lidos ?? 0,
        atualizados: res.atualizados ?? 0,
        criados: res.criados ?? 0,
        nao_casados: res.nao_casados ?? 0,
        conflitos_cpf: res.conflitos_cpf ?? 0,
        desligados: res.desligados ?? 0,
        cargos_vinculados: res.cargos_vinculados ?? 0,
        cargos_pendentes: res.cargos_pendentes ?? 0,
        loteAtual: 1,
        lotes: 1,
      })
      const pend = res.cargos_pendentes ?? 0
      setMsg(
        res.dry_run
          ? `Simulação concluída — nada foi gravado.${pend ? ` ${pend} com cargo Domínio pendente de cadastro.` : ''}`
          : `Importação concluída.${pend ? ` ${pend} colaborador(es) com código de cargo ainda não cadastrado.` : ''}`,
      )
      if (!res.dry_run) {
        await qc.invalidateQueries({ queryKey: ['rh', 'colaboradores'] })
        await qc.invalidateQueries({ queryKey: ['rh', 'kpis'] })
      }
    } catch (err) {
      setResultado(null)
      setErro(true)
      setMsg(err instanceof ApiError ? err.message : 'Falha na importação.')
    } finally {
      setPending(false)
    }
  }

  if (!editable) {
    return (
      <section className="hi-page">
        <PageHeader title="Importar colaboradores" subtitle="Acesso restrito a RH/Admin." />
        <Alert tone="danger">Sem permissão para importar.</Alert>
      </section>
    )
  }

  const detalhes = resultado?.detalhes || []
  const naoCasados = detalhes.filter((d) => d.status === 'nao_casado')
  const atualizadosDet = detalhes.filter((d) => d.status === 'ok' || d.campos?.length)
  const cargosPendentesDet = detalhes.filter((d) => d.cargo_status === 'pendente_cadastro')
  const codigosPendentes = resultado?.codigos_cargo_pendentes || []
  const pct =
    progresso && progresso.total > 0
      ? Math.min(100, Math.round((progresso.done / progresso.total) * 100))
      : pending
        ? 2
        : 0

  return (
    <section className="hi-page hi-import-colab-page">
      <PageHeader
        title="Importar colaboradores"
        subtitle="1º passo: Domínio WK1 ou CCusto (folha). Depois RHID (CPF/matrícula) e Bitrix (e-mail/foto + ACTIVE=ativo/desligado). Cargo Domínio vincula se já existir; senão fica pendente."
        actions={
          <Link className="btn btn-secondary" to="/app/rh">
            Voltar à lista
          </Link>
        }
      />

      <ImportPipelineNav active="dominio" />

      {msg ? <Alert tone={erro ? 'danger' : 'info'}>{msg}</Alert> : null}

      <Section title="Arquivo">
        <form
          className="hi-filters hi-import-form"
          onSubmit={(e) => {
            e.preventDefault()
            void executar()
          }}
        >
          <input
            type="file"
            accept=".wk1,.xls,.xlsx,.csv,application/vnd.ms-excel"
            aria-label="Arquivo de colaboradores"
            disabled={pending}
            onChange={(e) => {
              setFile(e.target.files?.[0] || null)
              setResultado(null)
              setProgresso(null)
              setMsg('')
              setErro(false)
            }}
          />
          <label className="hi-check">
            <input
              type="checkbox"
              checked={dryRun}
              disabled={pending}
              onChange={(e) => setDryRun(e.target.checked)}
            />
            Simular (dry-run)
          </label>
          <label className="hi-check">
            <input
              type="checkbox"
              checked={criarFaltantes}
              disabled={pending}
              onChange={(e) => setCriarFaltantes(e.target.checked)}
            />
            Criar quem não existir (se já existir, só atualiza)
          </label>
          <button type="submit" className="btn btn-primary" disabled={pending || !file}>
            {pending ? 'Importando…' : dryRun ? 'Simular importação' : 'Importar'}
          </button>
        </form>
        <Muted>
          {file
            ? `Selecionado: ${file.name} (${Math.round(file.size / 1024)} KB)`
            : 'Ex.: V1.wk1 (Domínio) ou Relatório Empregados CCusto.xls'}
          {' · '}
          Processamento em blocos de 40 registros.
        </Muted>

        {(pending || progresso) && (
          <div className="hi-import-progress" aria-live="polite">
            <div className="hi-import-progress-meta">
              <span>
                {progresso
                  ? `${progresso.done} / ${progresso.total} (${pct}%)`
                  : 'Preparando…'}
              </span>
              {progresso ? (
                <span>
                  Lote {progresso.loteAtual}/{progresso.lotes}
                </span>
              ) : null}
            </div>
            <div
              className="hi-import-progress-bar"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pct}
            >
              <div className="hi-import-progress-fill" style={{ width: `${pct}%` }} />
            </div>
            {progresso ? (
              <Muted>
                Atualizados {progresso.atualizados} · Criados {progresso.criados} · Sem match{' '}
                {progresso.nao_casados} · Desligados {progresso.desligados} · Cargos OK{' '}
                {progresso.cargos_vinculados} · Cargo pendente {progresso.cargos_pendentes}
                {progresso.conflitos_cpf
                  ? ` · Conflitos CPF ${progresso.conflitos_cpf}`
                  : ''}
              </Muted>
            ) : null}
          </div>
        )}
      </Section>

      {resultado ? (
        <>
          <KpiGrid>
            <Kpi label="Lidos" value={String(resultado.lidos ?? resultado.total ?? 0)} />
            <Kpi label="Atualizados" value={String(resultado.atualizados ?? 0)} />
            <Kpi label="Criados" value={String(resultado.criados ?? 0)} />
            <Kpi label="Cargos vinculados" value={String(resultado.cargos_vinculados ?? 0)} />
            <Kpi label="Cargo pendente" value={String(resultado.cargos_pendentes ?? 0)} />
            <Kpi label="Sem match" value={String(resultado.nao_casados ?? 0)} />
          </KpiGrid>

          {(resultado.cargos_pendentes ?? 0) > 0 ? (
            <Alert tone="info">
              Há códigos de cargo Domínio sem cadastro. Importe <strong>cargos.wk1</strong> (ou
              cadastre o cargo com o código) e reimporte os colaboradores para vincular.
            </Alert>
          ) : null}

          <Section title="Detalhes" flush>
            {!detalhes.length ? <Empty>Sem detalhes neste lote.</Empty> : null}
            {codigosPendentes.length > 0 ? (
              <div className="hi-import-detalhes">
                <Muted>Códigos de cargo que precisam ser cadastrados</Muted>
                <ul>
                  {codigosPendentes.map((cod) => (
                    <li key={cod}>
                      <strong>{cod}</strong> — código precisa ser cadastrado
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {cargosPendentesDet.length > 0 ? (
              <div className="hi-import-detalhes">
                <Muted>Colaboradores com cargo pendente (amostra)</Muted>
                <ul>
                  {cargosPendentesDet.slice(0, 40).map((d, i) => (
                    <li key={`cp-${i}`}>
                      <strong>{d.nome}</strong>
                      {d.codigo_cargo ? ` — código ${d.codigo_cargo}` : ''}
                      {' · precisa cadastrar o cargo'}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {atualizadosDet.length > 0 ? (
              <div className="hi-import-detalhes">
                <Muted>Atualizações (amostra)</Muted>
                <ul>
                  {atualizadosDet.slice(0, 40).map((d, i) => (
                    <li key={`ok-${i}`}>
                      <strong>{d.nome}</strong>
                      {d.campos?.length ? ` — ${d.campos.join(', ')}` : ''}
                      {d.match ? ` (${d.match})` : ''}
                      {d.cargo_status === 'vinculado' && d.codigo_cargo
                        ? ` · cargo OK (${d.codigo_cargo})`
                        : ''}
                      {d.cargo_status === 'pendente_cadastro' && d.codigo_cargo
                        ? ` · cargo pendente (${d.codigo_cargo})`
                        : ''}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {naoCasados.length > 0 ? (
              <div className="hi-import-detalhes">
                <Muted>Sem match (amostra)</Muted>
                <ul>
                  {naoCasados.slice(0, 40).map((d, i) => (
                    <li key={`nc-${i}`}>{d.nome}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </Section>

          {!resultado.dry_run ? (
            <Section title="Próximo passo">
              <Link className="btn btn-primary" to="/app/rh/importar/rhid">
                Ir para RHID
              </Link>
            </Section>
          ) : null}
        </>
      ) : null}
    </section>
  )
}
